import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { dashboardApi, mealApi, userApi } from "../lib/api";
import type { RecentActivityItem } from "../lib/types";

// Dashboard hooks
export const useDashboardStats = (date?: string) => {
  return useQuery({
    queryKey: ["dashboard", "stats", date],
    queryFn: () => dashboardApi.getStats(date),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes
  });
};

export const useSignupsChart = (date?: string) => {
  return useQuery({
    queryKey: ["dashboard", "signups-chart", date],
    queryFn: () => dashboardApi.getSignupsChart(date),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useFeatureUsage = (date?: string) => {
  return useQuery({
    queryKey: ["dashboard", "feature-usage", date],
    queryFn: () => dashboardApi.getFeatureUsage(date),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useDAUBreakdown = (date?: string) => {
  return useQuery({
    queryKey: ["dashboard", "dau-breakdown", date],
    queryFn: () => dashboardApi.getDAUBreakdown(date),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const usePeakUsageHours = (date?: string) => {
  return useQuery({
    queryKey: ["dashboard", "peak-usage-hours", date],
    queryFn: () => dashboardApi.getPeakUsageHours(date),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useTopUsers = (date?: string) => {
  return useQuery({
    queryKey: ["dashboard", "top-users", date],
    queryFn: () => dashboardApi.getTopUsers(date),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useRecentActivity = (date?: string) => {
  return useQuery({
    queryKey: ["dashboard", "recent-activity", date],
    queryFn: () => dashboardApi.getRecentActivity(date),
    staleTime: 1 * 60 * 1000, // 1 minute (live feed)
    refetchInterval: 1 * 60 * 1000,
  });
};

export const useUsersDashboard = (date?: string) => {
  return useQuery({
    queryKey: ["users-dashboard", date],
    queryFn: () => dashboardApi.getUsersDashboard(date),
    staleTime: 5 * 60 * 1000,
  });
};

// TODO: Add status and search params when backend is ready
// export const useUsersList = (date?: string, status?: string, search?: string)
export const useUsersList = (date?: string) => {
  return useQuery({
    queryKey: ["users-list", date],
    queryFn: () => dashboardApi.getUsersList(date),
    staleTime: 5 * 60 * 1000,
  });
};

export const useMealsDashboard = (date?: string) => {
  return useQuery({
    queryKey: ["meals-dashboard", date],
    queryFn: () => dashboardApi.getMealsDashboard(date),
    staleTime: 5 * 60 * 1000,
  });
};

export const useBiteBalanceTracking = (date?: string) => {
  return useQuery({
    queryKey: ["bite-balance-tracking", date],
    queryFn: () => dashboardApi.getBiteBalanceTracking(date),
    staleTime: 5 * 60 * 1000,
  });
};

export const useFitnessData = (date?: string) => {
  return useQuery({
    queryKey: ["fitness", date],
    queryFn: () => dashboardApi.getFitnessData(date),
    staleTime: 5 * 60 * 1000,
  });
};

// Meal hooks
export const useMeals = (params?: {
  date?: string;
  userId?: string;
  type?: string;
}) => {
  return useQuery({
    queryKey: ["meals", params],
    queryFn: () => mealApi.getAll(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useMeal = (id: string) => {
  return useQuery({
    queryKey: ["meal", id],
    queryFn: () => mealApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateMeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: mealApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meals"] });
    },
  });
};

export const useUpdateMeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      mealApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meals"] });
    },
  });
};

export const useDeleteMeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: mealApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meals"] });
    },
  });
};

// User hooks
export const useUsers = (params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  date?: string;
}) => {
  return useQuery({
    queryKey: ["users", params],
    queryFn: () => userApi.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: () => userApi.getById(id),
    enabled: !!id,
  });
};

export const useUserDetail = (id: string, date?: string) => {
  return useQuery({
    queryKey: ["user-detail", id, date],
    queryFn: () => userApi.getUserDetail(id, date),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
